/**
 * Schede "Attestati" e "Report" del dettaglio webinar: riepilogo, tabella con ricerca e filtro,
 * download, rigenerazione, revoca con motivo; elenco dei report con generazione manuale.
 */
import type { AttestatoAdmin, Report } from '@aulaviva/shared';
import { formattaCodice } from '@aulaviva/shared';
import { Alert, Badge, Button, DataTable, Dialog, EmptyState, NativeSelect, StatBlock, StatRow, TextArea, useToast, type ColumnDef, type VarianteBadge } from '@aulaviva/ui';
import { Award, Ban, CircleAlert, CircleCheck, Clock, Download, FileSpreadsheet, RefreshCw, Sparkles } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useAttestatiAdmin, useGeneraMancanti, useGeneraReport, useReportAdmin, useRevocaAttestato, useRiepilogoAttestati, useRigeneraAttestato } from '@/api/documenti';
import { formattaDataOra } from '@/lib/date';
import { admin, comuni, documenti } from '@/testi';
import { CampoRicerca } from './comuni';

type StatoAttestatoVista = 'in_coda' | 'pronto' | 'errore' | 'revocato';
function statoVista(a: AttestatoAdmin): StatoAttestatoVista {
  return a.revocatoAt ? 'revocato' : a.stato;
}

function BadgeAttestato({ a }: { a: AttestatoAdmin }) {
  const b = documenti.admin.attestati.badge;
  const s = statoVista(a);
  const map: Record<StatoAttestatoVista, { v: VarianteBadge; icona: typeof Clock }> = {
    in_coda: { v: 'warning', icona: Clock },
    pronto: { v: 'success', icona: CircleCheck },
    errore: { v: 'error', icona: CircleAlert },
    revocato: { v: 'error', icona: Ban },
  };
  return (
    <Badge variant={map[s].v} icon={map[s].icona}>
      {b[s]}
    </Badge>
  );
}

export function SchedaAttestati({ webinarId }: { webinarId: string }) {
  const t = documenti.admin.attestati;
  const toast = useToast();
  const [q, setQ] = useState('');
  const [stato, setStato] = useState('');
  const [pagina, setPagina] = useState(1);
  const riepilogo = useRiepilogoAttestati(webinarId);
  const attestati = useAttestatiAdmin(webinarId, q, stato, pagina);
  const revoca = useRevocaAttestato(webinarId);
  const rigenera = useRigeneraAttestato(webinarId);
  const generaMancanti = useGeneraMancanti(webinarId);
  const [daRevocare, setDaRevocare] = useState<AttestatoAdmin | null>(null);
  const [motivo, setMotivo] = useState('');
  const [erroreMotivo, setErroreMotivo] = useState<string | null>(null);
  const [dialogoMancanti, setDialogoMancanti] = useState(false);

  async function conferma(fn: () => Promise<unknown>, ok: string, chiudi: () => void) {
    try {
      await fn();
      chiudi();
      toast.mostra({ variant: 'success', title: ok });
    } catch (e) {
      chiudi();
      toast.mostra({ variant: 'error', title: e instanceof Error ? e.message : comuni.erroreGenerico });
    }
  }

  const colonne = useMemo<ColumnDef<AttestatoAdmin, unknown>[]>(
    () => [
      { header: t.colonne.nome, cell: ({ row }) => `${row.original.cognome} ${row.original.nome}` },
      { header: t.colonne.email, accessorKey: 'email' },
      { header: t.colonne.codice, cell: ({ row }) => <code className="t-code">{formattaCodice(row.original.codice)}</code> },
      {
        header: t.colonne.stato,
        cell: ({ row }) => (
          <div className="flex flex-col gap-1">
            <BadgeAttestato a={row.original} />
            {row.original.stato === 'errore' && row.original.errore && <span className="t-caption text-ink-muted">{row.original.errore}</span>}
            {row.original.revocaMotivo && <span className="t-caption text-ink-muted">{row.original.revocaMotivo}</span>}
          </div>
        ),
      },
      { header: t.colonne.emesso, cell: ({ row }) => (row.original.emessoAt ? formattaDataOra(row.original.emessoAt) : '—') },
      { header: t.colonne.emailInviata, cell: ({ row }) => (row.original.emailInviataAt ? t.emailInviata(formattaDataOra(row.original.emailInviataAt)) : t.emailNonInviata) },
      {
        header: t.colonne.azioni,
        cell: ({ row }) => {
          const a = row.original;
          const s = statoVista(a);
          return (
            <div className="flex flex-wrap gap-1">
              {s === 'pronto' && a.url && (
                <Button variant="discreet" icon={Download} onClick={() => window.open(a.url!, '_blank', 'noopener')}>
                  {t.scarica}
                </Button>
              )}
              {s === 'errore' && (
                <Button variant="discreet" icon={RefreshCw} loading={rigenera.isPending && rigenera.variables === a.id} onClick={() => conferma(() => rigenera.mutateAsync(a.id), t.rigenerato, () => undefined)}>
                  {t.rigenera}
                </Button>
              )}
              {s !== 'revocato' && (
                <Button variant="discreet" icon={Ban} onClick={() => { setDaRevocare(a); setMotivo(''); setErroreMotivo(null); }}>
                  {t.revoca}
                </Button>
              )}
            </div>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, rigenera.isPending, rigenera.variables],
  );
  const r = riepilogo.data;

  return (
    <section aria-labelledby="attestati-admin" className="flex flex-col gap-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex flex-col gap-1">
          <h2 id="attestati-admin" className="t-title-2">
            {t.titolo}
          </h2>
          <p className="t-body-small text-ink-muted">{t.sottotitolo}</p>
        </div>
        <Button icon={Sparkles} onClick={() => setDialogoMancanti(true)}>
          {t.generaMancanti}
        </Button>
      </div>
      {riepilogo.isError && <Alert variant="error" title={riepilogo.error.message} />}
      {r && (
        <StatRow>
          <StatBlock label={t.riepilogo.totale} value={r.totale} icon={Award} />
          <StatBlock label={t.riepilogo.pronti} value={r.pronti} icon={CircleCheck} tone="success" />
          <StatBlock label={t.riepilogo.inCoda} value={r.inCoda} icon={Clock} tone={r.inCoda > 0 ? 'warning' : 'neutral'} />
          <StatBlock label={t.riepilogo.errori} value={r.errori} icon={CircleAlert} tone={r.errori > 0 ? 'error' : 'neutral'} />
          <StatBlock label={t.riepilogo.revocati} value={r.revocati} icon={Ban} tone={r.revocati > 0 ? 'error' : 'neutral'} />
        </StatRow>
      )}
      <div className="grid grid-cols-1 md:grid-cols-[1fr_220px] gap-4 items-end">
        <CampoRicerca valore={q} onCambia={(v) => { setQ(v); setPagina(1); }} label={t.cerca} />
        <NativeSelect label={t.filtro} placeholder={t.tutti} value={stato} onChange={(e) => { setStato(e.target.value); setPagina(1); }} options={Object.entries(t.stati).map(([value, label]) => ({ value, label }))} />
      </div>
      {attestati.isError && <Alert variant="error" title={attestati.error.message} />}
      <DataTable
        caption={t.didascalia}
        columns={colonne}
        data={attestati.data?.elementi ?? []}
        getRowId={(a) => a.id}
        loading={attestati.isPending}
        testi={admin.comuni}
        pagination={attestati.data ? { pagina: attestati.data.pagina, perPagina: attestati.data.perPagina, totale: attestati.data.totale, onPagina: setPagina } : undefined}
        empty={<EmptyState icon={Award} title={!q && !stato ? t.nessuno : t.nessunRisultato} />}
      />
      <Dialog
        open={daRevocare !== null}
        onOpenChange={(o) => !o && setDaRevocare(null)}
        title={t.revocaTitolo}
        description={daRevocare ? t.revocaTesto(`${daRevocare.nome} ${daRevocare.cognome}`) : ''}
        actions={
          <>
            <Button onClick={() => setDaRevocare(null)}>{admin.comuni.annulla}</Button>
            <Button
              variant="primary"
              icon={Ban}
              loading={revoca.isPending}
              loadingText={admin.comuni.inCorso}
              onClick={() => {
                if (motivo.trim().length < 3) {
                  setErroreMotivo(t.revocaMotivo);
                  return;
                }
                if (daRevocare) void conferma(() => revoca.mutateAsync({ attestatoId: daRevocare.id, motivo: motivo.trim() }), t.revocato, () => setDaRevocare(null));
              }}
            >
              {t.revocaConferma}
            </Button>
          </>
        }
      >
        <TextArea label={t.revocaMotivo} value={motivo} onChange={(e) => { setMotivo(e.target.value); setErroreMotivo(null); }} error={erroreMotivo ?? undefined} maxLength={500} rows={3} />
      </Dialog>
      <Dialog
        open={dialogoMancanti}
        onOpenChange={(o) => !o && setDialogoMancanti(false)}
        title={t.generaMancantiTitolo}
        description={t.generaMancantiTesto}
        actions={
          <>
            <Button onClick={() => setDialogoMancanti(false)}>{admin.comuni.annulla}</Button>
            <Button variant="primary" icon={Sparkles} loading={generaMancanti.isPending} loadingText={admin.comuni.inCorso} onClick={() => conferma(async () => { const e = await generaMancanti.mutateAsync(); toast.mostra({ variant: 'success', title: t.generati(e.accodati) }); }, t.generati(0), () => setDialogoMancanti(false))}>
              {t.generaMancantiConferma}
            </Button>
          </>
        }
      />
    </section>
  );
}

function BadgeReport({ stato }: { stato: Report['stato'] }) {
  const b = documenti.admin.report.badge;
  const map: Record<Report['stato'], { v: VarianteBadge; icona: typeof Clock }> = {
    in_coda: { v: 'warning', icona: Clock },
    pronto: { v: 'success', icona: CircleCheck },
    errore: { v: 'error', icona: CircleAlert },
  };
  return (
    <Badge variant={map[stato].v} icon={map[stato].icona}>
      {b[stato]}
    </Badge>
  );
}

export function SchedaReport({ webinarId }: { webinarId: string }) {
  const t = documenti.admin.report;
  const toast = useToast();
  const report = useReportAdmin(webinarId);
  const genera = useGeneraReport(webinarId);
  const [dialogo, setDialogo] = useState(false);

  const colonne = useMemo<ColumnDef<Report, unknown>[]>(
    () => [
      {
        header: t.colonne.stato,
        cell: ({ row }) => (
          <div className="flex flex-col gap-1">
            <BadgeReport stato={row.original.stato} />
            {row.original.stato === 'errore' && row.original.errore && <span className="t-caption text-ink-muted">{row.original.errore}</span>}
          </div>
        ),
      },
      { header: t.colonne.generato, cell: ({ row }) => (row.original.generatoAt ? formattaDataOra(row.original.generatoAt) : '—') },
      { header: t.colonne.inviatoA, cell: ({ row }) => (row.original.inviatoA.length > 0 ? row.original.inviatoA.join(', ') : t.nonInviato) },
      { header: t.colonne.richiestoDa, cell: ({ row }) => row.original.richiestoDa ?? t.automatico },
      {
        header: t.colonne.file,
        cell: ({ row }) =>
          row.original.stato === 'pronto' && row.original.url ? (
            <Button variant="discreet" icon={Download} onClick={() => window.open(row.original.url!, '_blank', 'noopener')}>
              {t.scarica}
            </Button>
          ) : (
            <span className="t-body-small text-ink-muted">{row.original.nomeFile ?? '—'}</span>
          ),
      },
    ],
    [t],
  );

  async function generaOra() {
    try {
      await genera.mutateAsync();
      setDialogo(false);
      toast.mostra({ variant: 'success', title: t.messoInCoda });
    } catch (e) {
      setDialogo(false);
      toast.mostra({ variant: 'error', title: e instanceof Error ? e.message : comuni.erroreGenerico });
    }
  }

  return (
    <section aria-labelledby="report-admin" className="flex flex-col gap-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex flex-col gap-1">
          <h2 id="report-admin" className="t-title-2">
            {t.titolo}
          </h2>
          <p className="t-body-small text-ink-muted">{t.sottotitolo}</p>
        </div>
        <Button variant="primary" icon={FileSpreadsheet} onClick={() => setDialogo(true)}>
          {t.genera}
        </Button>
      </div>
      {report.isError && <Alert variant="error" title={report.error.message} />}
      <DataTable
        caption={t.didascalia}
        columns={colonne}
        data={report.data ?? []}
        getRowId={(r) => r.id}
        loading={report.isPending}
        testi={admin.comuni}
        empty={<EmptyState icon={FileSpreadsheet} title={t.nessuno} />}
      />
      <Dialog
        open={dialogo}
        onOpenChange={(o) => !o && setDialogo(false)}
        title={t.generaTitolo}
        description={t.generaTesto}
        actions={
          <>
            <Button onClick={() => setDialogo(false)}>{admin.comuni.annulla}</Button>
            <Button variant="primary" icon={FileSpreadsheet} onClick={generaOra} loading={genera.isPending} loadingText={admin.comuni.inCorso}>
              {t.generaConferma}
            </Button>
          </>
        }
      />
    </section>
  );
}
